// Token列表API响应接口
export interface TokenListItem {
  address0: string
  address1: string
  tokenName0: string
  tokenName1: string
  change24h01: string
  change24h10: string
  currentPrice01: number
  currentPrice10: number
  high24h01: number
  high24h10: number
  low24h01: number
  low24h10: number
  key: string
}

export interface TokenListResponse {
  status: boolean
  code: number
  msg: string
  data: {
    tokenList: TokenListItem[]
  }
}

// 允许的token地址集合
let allowedTokenAddresses: Set<string> = new Set()
let lastFetchTime = 0
const CACHE_DURATION = 5 * 60 * 1000 // 5分钟缓存

/**
 * 获取允许的token列表
 */
export async function fetchAllowedTokenList(): Promise<TokenListResponse> {
  try {
    const response = await fetch('https://kchartv2.linksay.io/public/tokenList', {
      method: 'GET',
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data: TokenListResponse = await response.json()
    
    // 更新允许的token地址集合
    updateAllowedTokenAddresses(data)
    
    return data
  } catch (error) {
    console.error('Failed to fetch token list:', error)
    throw error
  }
}

/**
 * 更新允许的token地址集合
 */
function updateAllowedTokenAddresses(tokenListResponse: TokenListResponse) {
  const newAddresses = new Set<string>()
  
  if (tokenListResponse.status && tokenListResponse.data?.tokenList) {
    tokenListResponse.data.tokenList.forEach(item => {
      // 添加两个token地址到允许列表
      if (item.tokenName0 === 'dc' || item.tokenName0 === 'wk') {
        newAddresses.add(item.address1.toLowerCase())
      } else {
        newAddresses.add(item.address0.toLowerCase())
      }
    })
  }
  
  allowedTokenAddresses = newAddresses
  lastFetchTime = Date.now()
  
  // 同时更新缓存的token列表数据
  updateCachedTokenList(tokenListResponse)
  
  console.log(`Updated allowed token list with ${newAddresses.size} addresses`)
}

/**
 * 检查token是否在允许列表中
 */
export function isTokenAllowed(address: string): boolean {
  if (!address) return false
  
  // 检查是否需要刷新缓存
  const now = Date.now()
  if (now - lastFetchTime > CACHE_DURATION) {
    // 异步刷新缓存，不阻塞当前检查
    fetchAllowedTokenList().catch(error => {
      console.warn('Failed to refresh token list cache:', error)
    })
  }
  
  return allowedTokenAddresses.has(address.toLowerCase())
}

/**
 * 获取当前允许的token地址集合
 */
export function getAllowedTokenAddresses(): Set<string> {
  return new Set(allowedTokenAddresses)
}

/**
 * 初始化token列表（应用启动时调用）
 */
export async function initializeTokenList(): Promise<void> {
  try {
    await fetchAllowedTokenList()
    console.log('Token list initialized successfully')
  } catch (error) {
    console.error('Failed to initialize token list:', error)
    // 初始化失败时，允许所有token（降级处理）
    allowedTokenAddresses = new Set()
  }
}

/**
 * 检查是否有缓存的token列表
 */
export function hasTokenListCache(): boolean {
  return allowedTokenAddresses.size > 0 && (Date.now() - lastFetchTime) < CACHE_DURATION
}

// 缓存的token列表数据
let cachedTokenList: TokenListItem[] = []

/**
 * 根据任一token地址查找对应的token对（来自缓存）
 */
export function findTokenPairByAddress(tokenAddress: string): TokenListItem | null {
  if (!tokenAddress) return null
  const normalized = tokenAddress.toLowerCase()
  const list = [...cachedTokenList]
  if (list.length === 0) return null
  return (
    list.find(item => (item.tokenName0 === 'dc' || item.tokenName0 === 'wk') ? item.address1.toLowerCase() === normalized : item.address0.toLowerCase() === normalized) ||
    null
  )
}

/**
 * 获取特定token的价格数据
 */
export function getTokenPriceData(tokenKey: string): {
  currentPrice: string
  change24h: string
  high24h: string
  low24h: string
  isPositive: boolean
  tokenName0: string
  tokenName1: string
  key: string
} | null {
  if (!tokenKey) {
    return null
  }
  console.log('tokenKey', tokenKey)

  // 创建缓存的快照，避免在查找过程中缓存被更新
  const tokenListSnapshot = [...cachedTokenList]
  
  if (tokenListSnapshot.length === 0) {
    return null
  }
  
  // 查找匹配的token对
  const tokenPair = tokenListSnapshot.find(item => 
    item.key.toLowerCase() === tokenKey
  )
  console.log('tokenpair', tokenPair)

  if (!tokenPair) {
    return null
  }
  let isSymbol0isValue =  tokenPair.tokenName0 === 'dc' || tokenPair.tokenName0 === 'wk'
  const currentPrice = isSymbol0isValue ? tokenPair.currentPrice10 : tokenPair.currentPrice01
  const change24h = isSymbol0isValue ? tokenPair.change24h10 : tokenPair.change24h01
  const high24h = isSymbol0isValue ? tokenPair.high24h10 : tokenPair.high24h01
  const low24h = isSymbol0isValue ? tokenPair.low24h10 : tokenPair.low24h01
  const tokenName0 = isSymbol0isValue ? tokenPair.tokenName0 : tokenPair.tokenName1.toUpperCase() 
  const tokenName1 = isSymbol0isValue ? tokenPair.tokenName1 : tokenPair.tokenName0.toUpperCase()
  const key = tokenPair.key
  // 解析变化百分比
  const changeValue = parseFloat(change24h.replace('%', ''))
  const isPositive = changeValue >= 0

  return {
    currentPrice: currentPrice.toFixed(6),
    change24h: Math.abs(changeValue).toFixed(2),
    high24h: high24h.toFixed(6),
    low24h: low24h.toFixed(6),
    isPositive,
    tokenName0,
    tokenName1,
    key
  }
}

/**
 * 更新缓存的token列表数据
 */
function updateCachedTokenList(tokenListResponse: TokenListResponse) {
  if (tokenListResponse.status && tokenListResponse.data?.tokenList) {
    cachedTokenList = tokenListResponse.data.tokenList
    console.log(`Updated cached token list with ${cachedTokenList.length} pairs`)
  }
}